/* eslint-disable indent */
import { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    Button,
    Stack,
    useTheme,
    useMediaQuery
} from "@mui/material";
import ConstructionIcon from "@mui/icons-material/Construction";

// Custom hook to fetch and manage maintenance alert
const useMaintenanceAlert = () => {
    const [alertData, setAlertData] = useState(null);
    const [showDialog, setShowDialog] = useState(false);
    const [loading, setLoading] = useState(true);
    const [userDismissed, setUserDismissed] = useState(false);
    const [message, setMessage] = useState("");

    // Fetch JSON data from S3
    useEffect(() => {
        const fetchMaintenanceData = async () => {
            try {
                const response = await fetch("https://prod-espro-public.s3.ap-south-1.amazonaws.com/Maintenance/status.json ", { cache: "no-store" });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                const modules = Array.isArray(data?.modules) ? data.modules : [];
                const erpModule = modules.find((m) => m?.name === "careers");
                let base = null;

                if (erpModule) {
                    if (erpModule.status === "on") {
                        base = erpModule;
                    } else if (data?.global?.status === "on") {
                        base = data.global;
                    }
                } else if (data?.global?.status === "on") {
                    base = data.global;
                }

                if (!base) {
                    setShowDialog(false);
                    return;
                }
                setAlertData(base);
                const now = new Date();
                const alertStart = base.alertStartTime ? new Date(base.alertStartTime) : null;
                const alertEnd = base.alertEndTime ? new Date(base.alertEndTime) : null;

                // Only show if we're inside the alert window
                if (alertStart && alertEnd && now >= alertStart && now <= alertEnd) {
                    // Format startTime/endTime into a user-friendly string with am/pm
                    const fmtOpts = {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                    };
                    const start = base.startTime ? new Date(base.startTime) : null;
                    const end = base.endTime ? new Date(base.endTime) : null;
                    const timeOpts = { hour: "numeric", minute: "2-digit", hour12: true };

                    const isSameDay = (a, b) =>
                        a.getFullYear() === b.getFullYear() &&
                        a.getMonth() === b.getMonth() &&
                        a.getDate() === b.getDate();

                    const formatSmart = (dt) => {
                        if (!dt) return "";
                        const now = new Date();
                        const tomorrow = new Date(now);
                        tomorrow.setDate(now.getDate() + 1);

                        if (isSameDay(dt, now)) {
                            return `Today ${dt.toLocaleTimeString(undefined, timeOpts)}`;
                        }
                        if (isSameDay(dt, tomorrow)) {
                            return `Tomorrow ${dt.toLocaleTimeString(undefined, timeOpts)}`;
                        }
                        // return dt.toLocaleString(undefined, fmtOpts);
                        return dt.toLocaleString("en-GB", fmtOpts);
                    };

                    let startStr = start ? formatSmart(start) : "";
                    let endStr = end ? formatSmart(end) : "";

                    // If both start and end are on the same day and that day is Today or Tomorrow,
                    // don't repeat the day label twice: "Today 10:00 AM to 1:00 PM" or "Tomorrow 10:00 AM to 1:00 PM"
                    if (start && end) {
                        const now = new Date();
                        const tomorrow = new Date(now);
                        tomorrow.setDate(now.getDate() + 1);

                        const startTimeOnly = start.toLocaleTimeString(undefined, timeOpts);
                        const endTimeOnly = end.toLocaleTimeString(undefined, timeOpts);

                        if (isSameDay(start, now) && isSameDay(end, now)) {
                            startStr = `Today ${startTimeOnly}`;
                            endStr = endTimeOnly;
                        } else if (isSameDay(start, tomorrow) && isSameDay(end, tomorrow)) {
                            startStr = `Tomorrow ${startTimeOnly}`;
                            endStr = endTimeOnly;
                        }
                    }

                    const rawMsg = base.applicationMessage || "";
                    const isValidDate = (d) => d instanceof Date && !isNaN(d.getTime());
                    const isTodayOrTomorrowString = (s) =>
                        typeof s === "string" && /^\s*(today|tomorrow)\b/i.test(s);

                    const displayStart = (isValidDate(start) || isTodayOrTomorrowString(startStr)) && startStr
                        ? startStr
                        : "--";
                    const displayEnd = (isValidDate(end) || isTodayOrTomorrowString(endStr)) && endStr
                        ? endStr
                        : "--";
                    const messageWithTimes = rawMsg
                        .replace(/<starttime>/gi, displayStart)
                        .replace(/<endtime>/gi, displayEnd);
                    setMessage(messageWithTimes);
                    // setShowDialog(true);
                }

            } catch (error) {
                console.error("Error fetching maintenance data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchMaintenanceData();
    }, []);

    // Check time conditions and update dialog state
    useEffect(() => {
        if (!alertData || alertData.status !== "on" || userDismissed) {
            return;
        }

        const checkTimeCondition = () => {
            const now = new Date();
            const startTime = new Date(alertData.alertStartTime);
            const endTime = new Date(alertData.alertEndTime);

            // If current time is past end time, don't show dialog
            if (now > endTime) {
                setShowDialog(false);
                return;
            }

            // If current time is equal to or greater than start time and before end time
            if (now >= startTime && now <= endTime) {
                // check if user already saw this alert (cookie set in handleClose)
                const seen = false;
                try {
                    if (typeof document !== "undefined" && alertData && alertData.alertEndTime) {
                        // const cookieName = `maintenance_seen_${encodeURIComponent(alertData.alertEndTime)}`;
                        // seen = document.cookie.split("; ").some((c) => c.split("=")[0] === cookieName);
                        // console.log(seen);
                    }
                } catch (err) {
                    console.error("Failed to read maintenance cookie:", err);
                }

                if (!seen) {
                    setShowDialog(true);
                } else {
                    setShowDialog(false);
                }
                return;
            }

            // Otherwise, don't show the dialog
            setShowDialog(false);
        };

        // Initial check
        checkTimeCondition();

        // Check every 30 seconds for precise timing
        const intervalId = setInterval(checkTimeCondition, 30000);

        // Cleanup interval on unmount
        return () => clearInterval(intervalId);
    }, [alertData, userDismissed]);

    const handleClose = (event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
        }
        setUserDismissed(true);
        setShowDialog(false);
        // if (alertData && alertData.alertEndTime) {
        //     try {
        //         const cookieName = `maintenance_seen_${encodeURIComponent(alertData.alertEndTime)}`;
        //         const expires = new Date(alertData.alertEndTime).toUTCString();
        //         let cookie = `${cookieName}=1; expires=${expires}; path=/; SameSite=Lax`;
        //         if (typeof window !== "undefined" && window.location && window.location.protocol === "https:") {
        //             cookie += "; Secure";
        //         }
        //         document.cookie = cookie;
        //     } catch (err) {
        //         console.error("Failed to set maintenance cookie:", err);
        //     }
        // }
    };

    return {
        showDialog,
        alertData,
        loading,
        handleClose, message
    };
};

// Main Maintenance Dialog Component
const MaintenanceAlertDialog = ({ s3Url }) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const {
        showDialog,
        alertData,
        loading,
        handleClose,
        message
    } = useMaintenanceAlert(s3Url);

    if (loading || !alertData) {
        return null;
    }

    return (
        <Dialog
            open={showDialog}
            onClose={handleClose}
            fullScreen={fullScreen}
            maxWidth="sm"
            fullWidth

            PaperProps={{
                sx: {
                    borderRadius: fullScreen ? 0 : 3,
                    overflow: "hidden"
                }
            }}
        >
            <DialogContent sx={{ p: 0 }}>
                <Box
                    sx={{
                        background: "linear-gradient(135deg, #0b3f64 0%, #084a73 100%)",
                        color: "white",
                        textAlign: "center",
                        py: 3,
                        px: 4
                    }}
                >
                    <Box
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 80,
                            height: 80,
                            borderRadius: "50%",
                            backgroundColor: "rgba(255, 255, 255, 0.2)",
                            mb: 3,
                            backdropFilter: "blur(10px)"
                        }}
                    >

                        <ConstructionIcon sx={{ fontSize: 40 }} />

                    </Box>

                    <Typography
                        variant="h4"
                        component="h1"
                        fontWeight="bold"
                        gutterBottom
                        sx={{
                            fontSize: { xs: "1.75rem", sm: "2.125rem" },
                            mb: 2
                        }}
                    >
                        Maintenance Alert
                    </Typography>
                </Box>

                <Box sx={{ px: 4, py: 2, backgroundColor: "background.paper" }}>
                    <Stack spacing={3} alignItems="center">
                        <Box
                            sx={{
                                backgroundColor: "warning.lighter",
                                border: 1,
                                borderColor: "warning.light",
                                borderRadius: 2,
                                p: 3,
                                width: "100%",
                                textAlign: "center"
                            }}
                        >
                            <Typography
                                variant="body1"
                                color="text.primary"
                                sx={{
                                    fontSize: { xs: "0.95rem", sm: "1.05rem" },
                                    lineHeight: 1.7,
                                    whiteSpace: "pre-line"
                                }}
                            >
                                {message}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: "grey.100",
                                borderRadius: 2,
                                p: 2.5,
                                width: "100%",
                                border: "1px dashed",
                                borderColor: "grey.300"
                            }}
                        >
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontStyle: "italic", textAlign: "center" }}
                            >
                                Some functionalities may not work as expected during maintenance time.
                                We appreciate your patience!
                            </Typography>
                        </Box>

                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleClose}
                            fullWidth
                            sx={{
                                py: 1.5,
                                fontSize: "1rem",
                                fontWeight: 600,
                                textTransform: "none",
                                borderRadius: 2,
                                boxShadow: 2,
                                background: "linear-gradient(135deg, #0b3f64 0%, #084a73 100%)",
                                "&:hover": {
                                    boxShadow: 4,
                                    transform: "translateY(-2px)",
                                    transition: "all 0.3s ease"
                                }
                            }}
                        >
                            I Understand
                        </Button>
                    </Stack>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default MaintenanceAlertDialog;
